import { FC, useState, useLayoutEffect, useEffect, useCallback } from 'react';
import { CaretDownOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import {
  AGE_BAND,
  FOUR_TIER,
  FOUR_TIER_COVERAGES,
  N_TIER,
  N_TIER_COVERAGES,
} from 'modules/benefitsWellness/constants/BenefitConstants';
import CoverageType from 'model/CoverageType';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import TierNameEditor from 'modules/benefitsWellness/components/TierNameEditor/TierNameEditor';
import { setSelectedCoverage } from 'modules/benefitsWellness/slices/benefitWellnessSlice';
import IconEdit from 'images/icon-edit.svg';
import useBenguide from 'hooks/benguide';
import { getNtierLabels } from 'modules/benefitsWellness/services/BenGuideService';
import {
  COVERAGE_TYPE_KEYS,
  WHO_IS_COVERED,
} from 'constants/languageConstants';
import { getText } from 'utils/stringUtil';
import styles from './whosCoverSelector.module.less';

type WhosCoverSelectorProps = {
  rateType: string;
  planListUpdateFlag?: boolean;
  nTiersCount?: number;
  planType?: string;
};

const WhosCoverSelector: FC<WhosCoverSelectorProps> = ({
  rateType,
  planListUpdateFlag,
  nTiersCount,
  planType,
}: WhosCoverSelectorProps) => {
  const [isOptionsOpen, setIsOptionOpen] = useState<boolean>(false);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const { selectedCoverageType } = useAppSelector(
    (state) => state.benefitWellness
  );
  const language = useAppSelector((state) => state.benguide.data?.language);

  const dispatch = useAppDispatch();
  const currentBenguide = useBenguide();

  const isEditMode = currentBenguide.isEditMode;

  const [customTierCoverages, setCustomTierCoverages] =
    useState(N_TIER_COVERAGES);

  const fetchTierLabels = useCallback(async () => {
    try {
      const response = await getNtierLabels(
        currentBenguide.masterId,
        planType?.toString().toUpperCase() || ''
      );

      const updatedCoverages = JSON.parse(JSON.stringify(N_TIER_COVERAGES));
      updatedCoverages[0].items = updatedCoverages[0].items.map(
        (item: any, index: number) => ({
          ...item,
          label: response.data[index],
          coverageLabel: `Covers ${
            response.data[index]?.toLowerCase() ||
            item.coverageLabel.toLowerCase()
          }`,
        })
      );

      setCustomTierCoverages(updatedCoverages);
      dispatch(setSelectedCoverage(updatedCoverages[0].items[0]));
    } catch (error) {
      console.error('Error fetching tier labels:', error);
    }
  }, [currentBenguide.masterId, planType, dispatch]);

  useEffect(() => {
    getNtierLabels(
      currentBenguide.masterId,
      planType?.toString().toUpperCase() || ''
    ).then((response) => {
      const updatedCoverages = JSON.parse(JSON.stringify(N_TIER_COVERAGES));
      updatedCoverages[0].items = updatedCoverages[0].items.map(
        (item: any, index: number) => ({
          ...item,
          label: response.data[index],
          coverageLabel: `Covers ${
            response.data[index]?.toLowerCase() ||
            item.coverageLabel.toLowerCase()
          }`,
        })
      );

      setCustomTierCoverages(updatedCoverages);
      dispatch(setSelectedCoverage(updatedCoverages[0].items[0]));
    });
  }, [currentBenguide.masterId, planType, dispatch]);

  useEffect(() => {
    fetchTierLabels();
  }, [fetchTierLabels]);

  const itemSelected = (type: CoverageType) => {
    dispatch(setSelectedCoverage(type));
    setIsOptionOpen(false);
  };

  useLayoutEffect(() => {
    if (isFirstLoad) {
      dispatch(setSelectedCoverage(FOUR_TIER_COVERAGES[0].items[0]));
      setIsFirstLoad(false);
    }
  }, [isFirstLoad, dispatch]);

  useEffect(() => {
    if (!isFirstLoad) {
      dispatch(setSelectedCoverage(FOUR_TIER_COVERAGES[0].items[0]));
    }
  }, [planListUpdateFlag, isFirstLoad, dispatch]);

  const handleOpenModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOptionOpen(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (rateType === AGE_BAND) {
    return null;
  }

  return (
    <div className={styles.whosCoverSelectorWrapper}>
      <div className={styles.dot}></div>
      <label className={styles.label}>
        {getText(WHO_IS_COVERED, language)}
      </label>

      <div
        className={styles.dropdownWrapper}
        onClick={(event) => {
          event.stopPropagation();
          setIsOptionOpen(!isOptionsOpen);
        }}
      >
        <div className={styles.selectedTypeLabel}>
          {rateType === N_TIER
            ? selectedCoverageType.label
            : getText(
                COVERAGE_TYPE_KEYS[
                  selectedCoverageType.nTierValue as keyof typeof COVERAGE_TYPE_KEYS
                ],
                language
              )}
          <CaretDownOutlined className={styles.icon} />
        </div>
        {isOptionsOpen && (
          <>
            <div
              className={styles.outside}
              onClick={() => {
                setIsOptionOpen(false);
              }}
            ></div>
            <div
              className={styles.overlay}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              {rateType === FOUR_TIER &&
                FOUR_TIER_COVERAGES[0].items.map((item) => {
                  return (
                    <div
                      key={item.value}
                      className={`${styles.item} ${
                        selectedCoverageType.value === item.value
                          ? styles.selected
                          : ''
                      }`}
                      onClick={() => {
                        itemSelected(item);
                      }}
                    >
                      {getText(
                        COVERAGE_TYPE_KEYS[
                          item.nTierValue as keyof typeof COVERAGE_TYPE_KEYS
                        ],
                        language
                      )}
                    </div>
                  );
                })}
              {rateType === N_TIER &&
                customTierCoverages[0].items
                  .slice(0, nTiersCount)
                  .map((item) => (
                    <div
                      key={item.value}
                      className={`${styles.item} ${
                        selectedCoverageType.value === item.value
                          ? styles.selected
                          : ''
                      }`}
                      onClick={() => {
                        itemSelected(item);
                      }}
                    >
                      {item.label}
                    </div>
                  ))}
              {rateType === N_TIER && isEditMode && (
                <div className={styles.editTierNamesBtn}>
                  <Button
                    onClick={handleOpenModal}
                    className={styles.tierNamesBtn}
                  >
                    <img src={IconEdit} alt="edit" />
                    <span className={styles.tierNamesBtnText}>
                      Edit Tier Names
                    </span>
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <TierNameEditor
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        coverages={customTierCoverages}
        planType={planType}
        masterId={currentBenguide.masterId}
        nTiersCount={nTiersCount || 7}
        onSaveSuccess={fetchTierLabels}
      />
    </div>
  );
};

export default WhosCoverSelector;
